import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Check, X, Eye, EyeOff, Wrench } from "lucide-react";
import {
  listarMotivosAjuste,
  criarMotivoAjuste,
  atualizarMotivoAjuste,
  excluirMotivoAjuste,
  MotivoAjuste,
} from "../services/ajustes";
import { useConfirm } from "../components/ConfirmProvider";
import { useAuth } from "../hooks/useAuth";

export function MotivosAjuste() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { can } = useAuth();
  const ehAdmin = can(["admin"]);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);

  const { data: motivos = [], isLoading } = useQuery({
    queryKey: ["motivos-ajuste", "todos"],
    queryFn: () => listarMotivosAjuste({ todos: true }),
  });

  const criarM = useMutation({
    mutationFn: criarMotivoAjuste,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["motivos-ajuste"] }); setNovo(false); },
  });
  const atualizarM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) => atualizarMotivoAjuste(id, dados),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["motivos-ajuste"] }); setEditandoId(null); },
  });
  const excluirM = useMutation({
    mutationFn: excluirMotivoAjuste,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["motivos-ajuste"] }),
    onError: (e: any) => confirm({ title: "Não foi possível excluir", message: e?.response?.data?.error?.message || "Erro", confirmText: "OK" }),
  });

  async function aoExcluir(m: MotivoAjuste) {
    const ok = await confirm({ title: `Excluir "${m.label}"?`, message: "Se houver ajustes com esse motivo, a exclusão é bloqueada — desative.", danger: true, confirmText: "Excluir" });
    if (ok) excluirM.mutate(m.id);
  }

  if (!can(["gestor", "admin"])) return <div className="text-slate-500">Acesso restrito.</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Wrench size={20} /> Motivos de ajuste
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Motivos escolhidos ao registrar um ajuste (interno/cliente). Alimentam o relatório de tempo segmentado por motivo.
          </p>
        </div>
        <button onClick={() => setNovo(true)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
          <Plus size={16} /> Novo motivo
        </button>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-xs">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Motivo</th>
              <th className="text-left px-4 py-2 font-medium">Slug</th>
              <th className="text-left px-4 py-2 font-medium">Ordem</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-right px-4 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody className="text-slate-700 dark:text-slate-200">
            {novo && <LinhaEditavel motivo={null} onSalvar={(d) => criarM.mutate(d)} onCancelar={() => setNovo(false)} salvando={criarM.isPending} />}
            {isLoading && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Carregando…</td></tr>}
            {!isLoading && motivos.length === 0 && !novo && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Nenhum motivo.</td></tr>}
            {motivos.map((m) =>
              editandoId === m.id ? (
                <LinhaEditavel key={m.id} motivo={m} onSalvar={(d) => atualizarM.mutate({ id: m.id, dados: d })} onCancelar={() => setEditandoId(null)} salvando={atualizarM.isPending} />
              ) : (
                <tr key={m.id} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-4 py-2 font-medium">{m.label}</td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-500">{m.slug}</td>
                  <td className="px-4 py-2 tabular-nums">{m.ordem}</td>
                  <td className="px-4 py-2">
                    {m.ativo
                      ? <span className="text-[11px] uppercase font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">Ativo</span>
                      : <span className="text-[11px] uppercase font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded">Inativo</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => atualizarM.mutate({ id: m.id, dados: { ativo: !m.ativo } })} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title={m.ativo ? "Desativar" : "Ativar"}>
                      {m.ativo ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                    <button onClick={() => setEditandoId(m.id)} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Editar"><Pencil size={14} /></button>
                    {ehAdmin && (
                      <button onClick={() => aoExcluir(m)} className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded" title="Excluir"><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LinhaEditavel({ motivo, onSalvar, onCancelar, salvando }: {
  motivo: MotivoAjuste | null;
  onSalvar: (dados: { label: string; ordem: number; ativo: boolean }) => void;
  onCancelar: () => void;
  salvando: boolean;
}) {
  const [label, setLabel] = useState(motivo?.label || "");
  const [ordem, setOrdem] = useState(motivo?.ordem ?? 0);
  const [ativo, setAtivo] = useState(motivo?.ativo ?? true);

  function salvar() {
    if (!label.trim()) return;
    onSalvar({ label: label.trim(), ordem: Number(ordem) || 0, ativo });
  }

  return (
    <tr className="border-t border-slate-100 dark:border-slate-800 bg-blue-50/40 dark:bg-blue-900/20">
      <td className="px-4 py-2">
        <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex.: Erro de ortografia" className="w-56 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500" autoFocus />
      </td>
      <td className="px-4 py-2 font-mono text-xs text-slate-400">{motivo ? motivo.slug : <em>(auto)</em>}</td>
      <td className="px-4 py-2">
        <input type="number" value={ordem} onChange={(e) => setOrdem(Number(e.target.value) || 0)} className="w-20 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500" />
      </td>
      <td className="px-4 py-2">
        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer"><input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} /> Ativo</label>
      </td>
      <td className="px-4 py-2 text-right">
        <button onClick={salvar} disabled={!label.trim() || salvando} className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded disabled:opacity-40" title="Salvar"><Check size={14} /></button>
        <button onClick={onCancelar} className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Cancelar"><X size={14} /></button>
      </td>
    </tr>
  );
}
